import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card, Choice, DataStatus, Parameter} from 'platform/components';
import {Box, Clickable, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  RecordAuditDefinitionResponseBody,
  RecordAuditResponseBody,
  useCreateRecordAuditMutation,
  useGetRecordAuditQuery,
  useListRecordAuditDefinitionQuery,
  useListRecordAuditsByRecordQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {
  ApiError,
  buildArray,
  getApiDateString,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {RecordAuditFillManager} from './RecordAuditFillManager';

const PANEL_MIN_HEIGHT = 60;

// Sentinel option in the audit switcher that opens the template picker instead of
// selecting an audit. Audits are keyed by uuid, so this can never collide.
const NEW_AUDIT_OPTION_VALUE = 'NEW_AUDIT';

// Newest first: the tab defaults to the latest audit, and the selector lists them in this order.
const byNewest = (a: RecordAuditResponseBody, b: RecordAuditResponseBody) =>
  b.createdAt.localeCompare(a.createdAt);

interface RecordAuditsProps extends RequiredTestIdProps {
  /** Resource/record pair the audits are listed for and created against (mirrors the API contract). */
  resourceId: string;
  recordId: string;
  branchId: string;
  isReadOnly?: boolean;
  /**
   * `flat` drops the participation/fill-panel card chrome — pass it when the embed already sits
   * inside a host card (e.g. a detail-page tab) so cards don't nest. Defaults to `card`.
   */
  variant?: 'card' | 'flat';
}

/**
 * Embeddable record-audit surface for any resource's detail page (e.g. the service order Audits
 * tab): audit switcher (newest default) with a "New audit" option, template picker filtered to
 * the resource, and the full fill experience via RecordAuditFillManager. The consumer owns tab
 * registration and feature-flag gating.
 */
export function RecordAudits(props: RecordAuditsProps) {
  // Toggled by the "New audit" switcher option so another audit can be created.
  const [isPicking, setIsPicking] = useState(false);
  // Which audit the tab shows when the record carries several (null → newest).
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);

  const formatDateTime = useDateTimeFormatter();

  const auditsQuery = useListRecordAuditsByRecordQuery(
    isNotNil(props.recordId) && props.recordId !== ''
      ? {resourceId: props.resourceId, recordId: props.recordId}
      : skipToken
  );

  const [createAudit, createAuditState] = useCreateRecordAuditMutation();

  const sortedAudits = [...(auditsQuery.data ?? [])].sort(byNewest);
  const auditCount = sortedAudits.length;
  const latestAudit = sortedAudits[0] ?? null;

  // Ignore a stale selection (e.g. after a refetch) and fall back to newest.
  const validSelectedId =
    isNotNil(selectedAuditId) && sortedAudits.some((audit) => audit.id === selectedAuditId)
      ? selectedAuditId
      : null;
  const displayedAuditId = isPicking ? null : (validSelectedId ?? latestAudit?.id ?? null);

  const auditOptions = buildArray<{label: string; value: string}>(
    sortedAudits.map((audit) => ({
      label: `${audit.auditNumber} · ${audit.definitionSnapshot.name} · ${formatDateTime(
        'dateShort',
        parseDate(audit.createdAt)
      )}`,
      value: audit.id,
    }))
  ).whenNot(props.isReadOnly, {
    label: i18n.t('page.recordAudit.actions.newAudit'),
    value: NEW_AUDIT_OPTION_VALUE,
  });

  const handleSelectOption = (value: string | null) =>
    match(value)
      .with(NEW_AUDIT_OPTION_VALUE, () => setIsPicking(true))
      .otherwise((auditId) => setSelectedAuditId(auditId));

  const handleCreate = (definitionId: string) => {
    createAudit({
      body: {
        recordAuditDefinitionId: definitionId,
        branchId: props.branchId,
        auditorEmployeeId: null,
        responsiblePersonEmployeeId: null,
        date: getApiDateString(new Date()),
        participants: [],
        recordId: {resourceId: props.resourceId, id: props.recordId},
      },
    })
      .unwrap()
      // Newest-first default: clearing the selection makes the tab show the new audit.
      .then(() => {
        setSelectedAuditId(null);
        setIsPicking(false);
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  return (
    <DataStatus
      isLoading={auditsQuery.isLoading}
      isError={auditsQuery.isError}
      minHeight={PANEL_MIN_HEIGHT}
    >
      {isNotNil(displayedAuditId) ? (
        // Keyed remount: useOptimisticAuditHeader inside the fill manager holds per-audit
        // overrides that must not survive switching to another audit.
        <AuditView
          key={displayedAuditId}
          auditId={displayedAuditId}
          isReadOnly={props.isReadOnly}
          variant={props.variant}
          auditOptions={auditOptions}
          selectedAuditId={displayedAuditId}
          onSelectOption={handleSelectOption}
          data-testid={suffixTestId('audit', props)}
        />
      ) : (
        <TemplatePicker
          resourceId={props.resourceId}
          isSubmitting={createAuditState.isLoading}
          onCancel={auditCount > 0 ? () => setIsPicking(false) : undefined}
          onConfirm={handleCreate}
          data-testid={suffixTestId('picker', props)}
        />
      )}
    </DataStatus>
  );
}

interface AuditViewProps extends RequiredTestIdProps {
  auditId: string;
  isReadOnly?: boolean;
  variant?: 'card' | 'flat';
  auditOptions: {label: string; value: string}[];
  selectedAuditId: string;
  onSelectOption: (value: string | null) => void;
}

function AuditView(props: AuditViewProps) {
  const auditQuery = useGetRecordAuditQuery({auditId: props.auditId});
  const audit = auditQuery.data;

  return (
    <DataStatus
      isLoading={auditQuery.isLoading}
      isError={auditQuery.isError}
      minHeight={PANEL_MIN_HEIGHT}
    >
      <Show when={audit}>
        {(audit) => (
          <VStack spacing={4}>
            {/* Tab framing: an audit switcher listing every audit on this order (newest default)
              plus a "New audit" option. The score bar and OK/BAD/NA checklist below come from
              RecordAuditFillPanel. */}
            <SelectedAuditChoice
              options={props.auditOptions}
              value={props.selectedAuditId}
              onChange={props.onSelectOption}
              data-testid={suffixTestId('auditSelect', props)}
            />
            <RecordAuditFillManager
              audit={audit}
              isReadOnly={props.isReadOnly}
              variant={props.variant}
            />
          </VStack>
        )}
      </Show>
    </DataStatus>
  );
}

interface SelectedAuditChoiceProps extends RequiredTestIdProps {
  options: {label: string; value: string}[];
  value: string;
  onChange: (auditId: string | null) => void;
}

// Fixed width so the dropdown reads as an audit switcher rather than stretching across the row.
const AUDIT_SELECT_WIDTH = 80;

function SelectedAuditChoice(props: SelectedAuditChoiceProps) {
  return (
    <Box width={AUDIT_SELECT_WIDTH}>
      <Choice
        value={props.value}
        options={props.options}
        onChange={props.onChange}
        isNotClearable
        menuInPortal
        data-testid={props['data-testid']}
      />
    </Box>
  );
}

interface TemplatePickerProps extends RequiredTestIdProps {
  resourceId: string;
  isSubmitting: boolean;
  onCancel?: () => void;
  onConfirm: (definitionId: string) => void;
}

function TemplatePicker(props: TemplatePickerProps) {
  const [definitionId, setDefinitionId] = useState<string | null>(null);
  const definitionsQuery = useListRecordAuditDefinitionQuery({});

  // A definition with no resourceIds is universal; otherwise it must list this resource.
  const eligibleDefinitions = (definitionsQuery.data ?? []).filter(
    (definition: RecordAuditDefinitionResponseBody) =>
      isEmpty(definition.resourceIds) || definition.resourceIds.includes(props.resourceId)
  );

  return (
    <VStack spacing={4}>
      <VStack spacing={1}>
        <Heading size={4}>
          {i18n.t('page.recordAudit.create.wizard.steps.definition.title')}
        </Heading>
        <Text size="small" color="secondary">
          {i18n.t('page.recordAudit.create.wizard.steps.definition.description')}
        </Text>
      </VStack>
      <DataStatus
        isLoading={definitionsQuery.isLoading}
        isEmpty={isEmpty(eligibleDefinitions)}
        emptyMessage={i18n.t('page.recordAudit.create.wizard.noDefinition.title')}
        emptySubheadline={i18n.t('page.recordAudit.create.wizard.noDefinition.description')}
        minHeight={PANEL_MIN_HEIGHT}
      >
        <VStack spacing={3}>
          {eligibleDefinitions.map((definition) => (
            <Clickable
              key={definition.id}
              width="100%"
              onClick={() => setDefinitionId(definition.id)}
              data-testid={suffixTestId(`template-${definition.id}`, props)}
            >
              <Card
                variant="inlineWhite"
                title={definition.name}
                subtitle={definition.description ?? undefined}
                flags={
                  isNotNil(definition.category)
                    ? [{label: definition.category, colorScheme: 'neutral', isSubtle: true}]
                    : undefined
                }
                parameters={buildArray<Parameter>()
                  .add(
                    i18n.t('page.recordAudit.create.wizard.taskCount', {
                      count: definition.tasks.length,
                    })
                  )
                  .add(
                    i18n.t('page.recordAudit.create.wizard.passThreshold', {
                      threshold: definition.successThreshold,
                    })
                  )}
                control={{
                  type: 'radio',
                  value: definition.id === definitionId,
                  onChange: () => setDefinitionId(definition.id),
                }}
              />
            </Clickable>
          ))}
        </VStack>
      </DataStatus>
      <HStack spacing={2}>
        <Button
          variant="primary"
          title={i18n.t('general.actions.create')}
          isDisabled={isNil(definitionId)}
          isLoading={props.isSubmitting}
          onClick={() => {
            if (isNotNil(definitionId)) {
              props.onConfirm(definitionId);
            }
          }}
          data-testid={suffixTestId('confirm', props)}
        />
        <Show when={isNotNil(props.onCancel)}>
          <Button
            variant="secondary"
            title={i18n.t('general.actions.cancel')}
            onClick={props.onCancel}
            data-testid={suffixTestId('cancel', props)}
          />
        </Show>
      </HStack>
    </VStack>
  );
}
